import React, { useState, useEffect } from 'react';
import { checkSetup, setup } from '../../../shared/api/authApi';
import '../../../styles/features/auth/Setup.css';

function Setup({ onSetupComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: 'My Status Page',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: '',
    publicUrl: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpFromEmail: '',
    smtpFromName: '',
    smtpTlsMode: 'starttls',
    smtpTimeoutMs: 10000,
  });

  useEffect(() => {
    checkSetupStatus();
  }, []);

  async function checkSetupStatus() {
    try {
      const response = await checkSetup();
      // If we get a 200 response, setup is required - proceed with setup
      if (response.status === 200) {
        setLoading(false);
      }
    } catch (err) {
      // If we get a 400, setup is already complete
      if (err.response?.status === 400) {
        setError('Setup has already been completed');
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
  }

  async function handleSetup(e) {
    e.preventDefault();

    if (formData.adminPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.adminPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
       const response = await setup({
         title: formData.title,
         adminName: formData.adminName,
         adminEmail: formData.adminEmail,
         adminPassword: formData.adminPassword,
         publicUrl: formData.publicUrl,
         smtpHost: formData.smtpHost,
         smtpPort: formData.smtpPort,
         smtpUser: formData.smtpUser,
         smtpPassword: formData.smtpPassword,
         smtpFromEmail: formData.smtpFromEmail,
         smtpFromName: formData.smtpFromName,
         smtpTlsMode: formData.smtpTlsMode,
         smtpTimeoutMs: formData.smtpTimeoutMs,
       });

      localStorage.setItem('token', response.data.token);
      onSetupComplete();
    } catch (err) {
      setError(err.response?.data?.error || 'Setup failed');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <div className="setup-container"><div className="setup-loading">Checking setup status...</div></div>;
  }

  if (error) {
    return <div className="setup-container"><div className="setup-error">{error}</div></div>;
  }

  return (
    <div className="setup-container">
      <div className="setup-wrapper">
        <div className="setup-header">
          <h1>Status Page Setup</h1>
          <p className="setup-subtitle">Welcome! Let's get your status page configured</p>
        </div>

        <div className="setup-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Site Settings</div>
          </div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Admin Account</div>
          </div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Email (Optional)</div>
          </div>
        </div>

        <form onSubmit={handleSetup} className="setup-form">
          {step === 1 && (
            <div className="setup-fields">
              <h2>Site Settings</h2>
               <div className="form-group">
                 <label>Website Title</label>
                 <input
                   type="text"
                   value={formData.title}
                   onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                   placeholder="My Status Page"
                   required
                 />
                 <small>This appears on your status page</small>
               </div>
               <div className="form-group">
                 <label>Public Status Page URL</label>
                 <input
                   type="url"
                   value={formData.publicUrl}
                   onChange={(e) => setFormData({ ...formData, publicUrl: e.target.value })}
                   placeholder="https://status.example.com"
                 />
                 <small>Used in email notifications (defaults to localhost if not set).</small>
               </div>
               <button type="button" onClick={() => setStep(2)} className="btn-next">
                Next →
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="setup-fields">
              <h2>Create Admin Account</h2>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.adminName}
                  onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                  placeholder="Your Name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                  placeholder="admin@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={formData.adminPassword}
                  onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength="8"
                />
                <small>Minimum 8 characters, with uppercase, lowercase, number, and special character</small>
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  required
                />
              </div>
              <div className="button-group">
                <button type="button" onClick={() => setStep(1)} className="btn-back">
                  ← Back
                </button>
                <button type="button" onClick={() => setStep(3)} className="btn-next">
                  Next →
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="setup-fields">
              <h2>Email Notifications (Optional)</h2>
              <p className="step-info">Configure SMTP to send email notifications. You can skip this for now.</p>
              
              <div className="form-group">
                <label>SMTP Host</label>
                <input
                  type="text"
                  value={formData.smtpHost}
                  onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                  placeholder="smtp.gmail.com"
                />
                <small>e.g., smtp.gmail.com or your provider</small>
              </div>
              <div className="form-group">
                <label>SMTP Port</label>
                <input
                  type="number"
                  value={formData.smtpPort}
                  onChange={(e) => setFormData({ ...formData, smtpPort: parseInt(e.target.value) })}
                  placeholder="587"
                />
              </div>
              <div className="form-group">
                <label>SMTP Username</label>
                <input
                  type="text"
                  value={formData.smtpUser}
                  onChange={(e) => setFormData({ ...formData, smtpUser: e.target.value })}
                  placeholder="your-email@gmail.com"
                />
              </div>
              <div className="form-group">
                <label>From Email</label>
                <input
                  type="email"
                  value={formData.smtpFromEmail}
                  onChange={(e) => setFormData({ ...formData, smtpFromEmail: e.target.value })}
                  placeholder="status@example.com"
                />
              </div>
              <div className="form-group">
                <label>From Name</label>
                <input
                  type="text"
                  value={formData.smtpFromName}
                  onChange={(e) => setFormData({ ...formData, smtpFromName: e.target.value })}
                  placeholder="Status Bot"
                />
              </div>
              <div className="form-group">
                <label>TLS Mode</label>
                <select
                  value={formData.smtpTlsMode}
                  onChange={(e) => setFormData({ ...formData, smtpTlsMode: e.target.value })}
                >
                  <option value="starttls">STARTTLS</option>
                  <option value="tls">TLS</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div className="form-group">
                <label>SMTP Password</label>
                <input
                  type="password"
                  value={formData.smtpPassword}
                  onChange={(e) => setFormData({ ...formData, smtpPassword: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="form-group">
                <label>SMTP Timeout (ms)</label>
                <input
                  type="number"
                  min="1000"
                  max="120000"
                  value={formData.smtpTimeoutMs}
                  onChange={(e) => setFormData({ ...formData, smtpTimeoutMs: parseInt(e.target.value, 10) || 10000 })}
                  placeholder="10000"
                />
              </div>

              <div className="button-group">
                <button type="button" onClick={() => setStep(2)} className="btn-back">
                  ← Back
                </button>
                <button type="submit" className="btn-complete" disabled={loading}>
                  {loading ? 'Setting up...' : 'Complete Setup'}
                </button>
              </div>
            </div>
          )}

          {error && <div className="setup-error-message">{error}</div>}
        </form>
      </div>
    </div>
  );
}

export default Setup;
